import { useCallback, useEffect, useState } from 'react';
import { api, BladApi, type ListaUrzadzen, type Sesja, type StatusUrzadzenia } from '../api';
import { data } from '../format';
import { idz } from '../router';
import { SkanQr } from '../components/SkanQr';
import { NaglowekWidoku } from '../components/Shell';
import { IkonaPlus, IkonaSkan } from '../components/Icons';
import {
  ChipFiltra,
  ChipStatusu,
  KOLEJNOSC_STATUSOW,
  Karta,
  Komunikat,
  Przycisk,
  PrzyciskMarki,
  Pusto,
  nazwaStatusu,
} from '../components/ui';

const NA_STRONE = 30;

/**
 * Lista urządzeń — punkt wejścia do rdzenia produktu.
 *
 * Szukanie mieszka w pasku powłoki (jedno pole na całą aplikację), tu zostają
 * filtry stanu jako chipy z licznikami. Statusu nie da się z listy zmienić —
 * wynika z pracy serwisanta (CLAUDE.md §1 zasada 3).
 */
export function DevicesView({ sesja, szukane = '' }: { sesja: Sesja; szukane?: string }) {
  const mozeZakladac = sesja.permissions.includes('device.write');

  const [status, setStatus] = useState<StatusUrzadzenia | null>(null);
  const [strona, setStrona] = useState(0);
  const [wynik, setWynik] = useState<ListaUrzadzen | null>(null);
  const [dosypane, setDosypane] = useState<ListaUrzadzen['urzadzenia']>([]);
  const [blad, setBlad] = useState<string | null>(null);
  const [skanuje, setSkanuje] = useState(false);

  useEffect(() => {
    setStrona(0);
    setDosypane([]);
  }, [szukane, status]);

  useEffect(() => {
    let aktualne = true;
    setBlad(null);

    api
      .urzadzenia({
        q: szukane,
        status: status ?? undefined,
        limit: NA_STRONE,
        offset: strona * NA_STRONE,
      })
      .then((odpowiedz) => {
        if (!aktualne) return;
        setWynik(odpowiedz);
        setDosypane((poprzednie) =>
          strona === 0 ? odpowiedz.urzadzenia : [...poprzednie, ...odpowiedz.urzadzenia],
        );
      })
      .catch((error: unknown) => {
        if (aktualne) {
          setBlad(error instanceof BladApi ? error.message : 'Nie udało się wczytać listy.');
        }
      });

    return () => {
      aktualne = false;
    };
  }, [szukane, status, strona]);

  const zeSkanu = useCallback((token: string) => {
    setSkanuje(false);
    api
      .urzadzeniePoQr(token)
      .then((karta) => idz(`/urzadzenia/${karta.urzadzenie.id}`))
      .catch((error: unknown) => {
        setBlad(error instanceof BladApi ? error.message : 'Nie rozpoznano kodu.');
      });
  }, []);

  const podsumowanie = wynik?.podsumowanie;

  return (
    <>
      <NaglowekWidoku
        tytul="Urządzenia"
        podtytul={
          szukane
            ? `Szukanie: ${szukane}`
            : podsumowanie
              ? `${podsumowanie.wszystkie} kart · rdzeń produktu`
              : undefined
        }
        akcje={
          <>
            <Przycisk
              type="button"
              onClick={() => setSkanuje((czy) => !czy)}
              ikona={<IkonaSkan rozmiar={16} />}
            >
              {skanuje ? 'Zamknij' : 'Skanuj'}
            </Przycisk>
            {mozeZakladac && (
              <PrzyciskMarki
                type="button"
                onClick={() => idz('/urzadzenia/nowe')}
                ikona={<IkonaPlus rozmiar={16} />}
              >
                Nowa karta
              </PrzyciskMarki>
            )}
          </>
        }
      />

      {skanuje && (
        <div className="mb-14">
          <SkanQr onToken={zeSkanu} onZamknij={() => setSkanuje(false)} />
        </div>
      )}

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <div className="mb-14 flex flex-wrap items-center gap-7">
        <ChipFiltra
          type="button"
          aktywny={status === null}
          licznik={podsumowanie?.wszystkie}
          onClick={() => setStatus(null)}
        >
          Wszystkie
        </ChipFiltra>
        {KOLEJNOSC_STATUSOW.map((kod) => (
          <ChipFiltra
            key={kod}
            type="button"
            aktywny={status === kod}
            licznik={podsumowanie?.wgStatusu[kod] ?? 0}
            onClick={() => setStatus(kod)}
          >
            {nazwaStatusu(kod)}
          </ChipFiltra>
        ))}
        <span className="t-small ml-auto text-n-500">
          Statusu nie da się zmienić z listy — wynika z pracy serwisanta.
        </span>
      </div>

      <Karta wypelnienie={false}>
        {wynik === null && <Pusto tresc="Wczytywanie…" />}

        {wynik !== null && dosypane.length === 0 && (
          <Pusto
            tresc={
              szukane || status
                ? 'Nic nie pasuje do tych warunków.'
                : 'Nie ma jeszcze żadnej karty. Pierwsza powstanie przy przyjęciu maszyny na serwis albo z montażu.'
            }
          />
        )}

        {dosypane.length > 0 && (
          <div className="overflow-x-auto">
            <table className="tab">
              <thead>
                <tr>
                  <th>Maszyna</th>
                  <th>Nr boczny</th>
                  <th>Nr fabryczny</th>
                  <th>Klient</th>
                  <th>Przegląd</th>
                  <th>Do uzupełnienia</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dosypane.map((urzadzenie) => (
                  <tr
                    key={urzadzenie.id}
                    data-klikalny=""
                    tabIndex={0}
                    role="link"
                    onClick={() => idz(`/urzadzenia/${urzadzenie.id}`)}
                    onKeyDown={(zdarzenie) => {
                      if (zdarzenie.key === 'Enter' || zdarzenie.key === ' ') {
                        zdarzenie.preventDefault();
                        idz(`/urzadzenia/${urzadzenie.id}`);
                      }
                    }}
                  >
                    <td>
                      <div className="t-body-strong text-n-900">
                        {urzadzenie.producent} {urzadzenie.model}
                      </div>
                      <div className="t-small text-n-600">{urzadzenie.category.nazwa}</div>
                    </td>
                    <td className="t-mono-data whitespace-nowrap text-n-900">
                      {urzadzenie.sideNumber}
                    </td>
                    <td className="t-mono-data whitespace-nowrap text-n-600">
                      {urzadzenie.serialNumber}
                    </td>
                    <td className="t-small text-n-700">{urzadzenie.customer.nazwa}</td>
                    <td className="t-mono-data whitespace-nowrap text-n-600">
                      {data(urzadzenie.nastepnyPrzeglad) || '—'}
                    </td>
                    <td className="t-mono-data text-n-600">
                      {urzadzenie.braki > 0 ? urzadzenie.braki : '—'}
                    </td>
                    <td>
                      <ChipStatusu status={urzadzenie.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {wynik !== null && dosypane.length < wynik.ile && (
          <div className="border-t border-n-100 px-14 py-10">
            <Przycisk type="button" onClick={() => setStrona((s) => s + 1)}>
              Pokaż kolejne ({wynik.ile - dosypane.length})
            </Przycisk>
          </div>
        )}
      </Karta>
    </>
  );
}
